// Campaign status utility functions
export type CampaignStatus = 'draft' | 'submitted' | 'preparing' | 'running' | 'completed';

export const CAMPAIGN_STATUSES: Record<CampaignStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  preparing: 'Preparing',
  running: 'Running',
  completed: 'Completed'
};

export const STATUS_COLORS: Record<CampaignStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  preparing: 'bg-yellow-100 text-yellow-800',
  running: 'bg-green-100 text-green-800',
  completed: 'bg-purple-100 text-purple-800'
};

export const STATUS_DESCRIPTIONS: Record<CampaignStatus, string> = {
  draft: 'Campaign is being created and can be edited',
  submitted: 'Campaign has been submitted and is awaiting review',
  preparing: 'Campaign is being prepared for launch',
  running: 'Campaign is currently active and running',
  completed: 'Campaign has been completed'
};

// Check if campaign is editable based on status and user role
export function isCampaignEditable(
  status: CampaignStatus,
  userRole: string,
  userId: string,
  campaignUserId: string,
  assignedCSR?: string
): boolean {
  // Admin can edit all campaigns
  if (userRole === 'admin') {
    return true;
  }
  
  // Campaign must be in editable status
  if (!['draft', 'submitted', 'preparing'].includes(status)) {
    return false;
  }
  
  // User can edit their own campaigns
  if (userId === campaignUserId) {
    return true;
  }
  
  // CSR can edit assigned campaigns
  if (userRole === 'csr' && assignedCSR === userId) {
    return true;
  }
  
  return false;
}

// Get button text based on campaign status and editability
export function getCampaignButtonText(isEditable: boolean): string {
  if (isEditable) {
    return 'Edit Campaign';
  }
  
  return 'View Content Details';
}

// Get available status transitions for admin
export function getAvailableStatusTransitions(currentStatus: CampaignStatus): CampaignStatus[] {
  const allStatuses: CampaignStatus[] = ['draft', 'submitted', 'preparing', 'running', 'completed'];
  return allStatuses.filter(status => status !== currentStatus);
}

// Check if user can submit campaign (only draft status)
export function canSubmitCampaign(status: CampaignStatus): boolean {
  return status === 'draft';
}

// Get next logical status in workflow
export function getNextStatus(currentStatus: CampaignStatus): CampaignStatus | null {
  const workflow: Record<CampaignStatus, CampaignStatus | null> = {
    draft: 'submitted',
    submitted: 'preparing',
    preparing: 'running',
    running: 'completed',
    completed: null
  };
  
  return workflow[currentStatus];
}
